import { useState } from "react";
import { Pencil, Check, BadgeCheck, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ProfileNameProps {
  fullName: string;
  username: string;
  isPro: boolean;
  isOwnProfile: boolean;
  onSave: (name: string) => Promise<void>;
  userId: string;
}

interface Badge {
  name: string;
  description: string;
}

interface DisplayedBadge {
  badges: Badge;
}

export const ProfileName = ({ fullName, username, isPro, isOwnProfile, onSave, userId }: ProfileNameProps) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(fullName);

  const formatUsername = (username: string) => {
    return username.toLowerCase().replace(/\s+/g, '_');
  };

  const formattedUsername = formatUsername(username);

  const { data: displayedBadges } = useQuery({
    queryKey: ["displayed-badges", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_badges")
        .select(`
          badges (
            name,
            description
          )
        `)
        .eq("user_id", userId)
        .eq("is_displayed", true);

      if (error) throw error;
      
      return (data as unknown as DisplayedBadge[]) || [];
    },
    enabled: !!userId,
  });

  const handleSave = async () => {
    await onSave(editedName);
    setIsEditingName(false);
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        {isEditingName ? (
          <div className="flex items-center gap-2">
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="text-3xl font-bold bg-transparent border-flux-purple/20"
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={handleSave}
              className="text-flux-purple hover:text-flux-purple/80"
            >
              <Check className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">{fullName}</h1>
            {isPro && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <BadgeCheck className="h-6 w-6 text-yellow-500 drop-shadow-[0_0_5px_rgba(234,179,8,0.5)] animate-pulse filter brightness-125" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Pro Member</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {displayedBadges?.map((badge, index) => (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger>
                    <Award className="h-5 w-5 text-flux-cyan" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{badge.badges.name}</p>
                    <p className="text-xs text-gray-400">{badge.badges.description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            {isOwnProfile && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setIsEditingName(true)}
                className="text-flux-purple hover:text-flux-purple/80"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      <p className="text-gray-400">@{formattedUsername}</p>
    </div>
  );
};
